import React from 'react';
import { makeStyles } from 'tss-react/mui';
import useWindowWidth from 'components/hooks/useWindowWidth';
import { PageScrollContext } from './PageScrollContext';

const useStyles = makeStyles<void, 'safeAreaInset'>()(
    (theme, _params, classes) => ({
        base: {
            position: 'relative',
            display: 'flex',
            width: '100%',
            minHeight: '100%',
            height: '100%',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            background: theme.palette.background.backgroundColor,
        },
        shell: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            minHeight: 0,
            minWidth: 0,
        },
        scrollContainer: {
            width: '100%',
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            height: '100%',
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
        },
        body: {
            // Keep the footer relative to the full content, inside the scroller.
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            minHeight: '100%',
            flexGrow: 1,
            flexShrink: 0,
        },
        mobile: {
            userSelect: 'none',
            touchAction: 'manipulation',
            [`&.${classes.safeAreaInset}`]: {
                paddingTop: 'env(safe-area-inset-top, 0px)',
                paddingLeft: 'env(safe-area-inset-left, 0px)',
                paddingRight: 'env(safe-area-inset-right, 0px)',
            },
        },
        safeAreaInset: {},
    })
);

interface Props {
    children: JSX.Element | Array<JSX.Element>;
    className?: string;
    ignoreSafeAreaInset?: boolean;
    scrollRef?: React.RefObject<HTMLDivElement>;
}

export default function Page({
    children,
    className,
    ignoreSafeAreaInset,
    scrollRef,
}: Props) {
    const { classes, cx } = useStyles();
    const { isMobile } = useWindowWidth();
    const internalScrollRef = React.useRef<HTMLDivElement>(null);
    const scrollContainerRef = scrollRef ?? internalScrollRef;

    return (
        <div
            className={cx(
                classes.base,
                {
                    [classes.mobile]: isMobile,
                    [classes.safeAreaInset]: !ignoreSafeAreaInset,
                },
                className
            )}>
            <PageScrollContext.Provider value={scrollContainerRef}>
                <div className={classes.shell}>
                    <div
                        ref={scrollContainerRef}
                        className={classes.scrollContainer}>
                        <div className={classes.body}>{children}</div>
                    </div>
                </div>
            </PageScrollContext.Provider>
        </div>
    );
}
